import React, { useEffect, useMemo, useRef, useState } from "react";
import "./TimelinePage.css";
import DecadeNavBar from "./DecadeNavBar";
import TimelineImage from "./TimelineImage";
import timelineImageManifest from "./timelineImageManifest.json";

function TimelinePage({
  decades: propDecades,
  activeDecade: propActiveDecade,
  onDecadeClick: propOnDecadeClick,
  decadeRefs: propDecadeRefs,
  setActiveDecade: propSetActiveDecade,
  setDecades,
  isMenuHidden,
  language = "EN",
  targetEventId,
}) {
  const MOBILE_DECADE_BREAKPOINT = 768;
  const [timelineData, setTimelineData] = useState(null);
  const [activeDecade, setActiveDecade] = useState("");
  const decadeRefs = useRef({});
  const eventRefs = useRef({});
  const lastLocatedEventRef = useRef(null);
  // 新增：decade navbar 显隐状态
  const [showDecadeNav, setShowDecadeNav] = useState(() =>
    window.innerWidth > MOBILE_DECADE_BREAKPOINT
  );
  const firstEventBlockRef = useRef(null);
  const firstEventImageRef = useRef(null);
  // 新增：控制DecadeNavBar模式
  const [showCompactDecadeBar, setShowCompactDecadeBar] = useState(false);

  // Lightbox state for clicking timeline images
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState("");
  const [lightboxCopyright, setLightboxCopyright] = useState("");
  const [lightboxExiting, setLightboxExiting] = useState(false);

  const openLightbox = (src, alt, copyrightText) => {
    if (!src) return;
    setLightboxExiting(false);
    setLightboxSrc(src);
    setLightboxAlt(alt || "");
    setLightboxCopyright(copyrightText || "");
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxExiting(true);
    setTimeout(() => {
      setLightboxOpen(false);
      setLightboxExiting(false);
      setLightboxSrc(null);
      setLightboxAlt("");
      setLightboxCopyright("");
    }, 380);
  };

  // Close on ESC and lock scroll when overlay shown
  useEffect(() => {
    if (!(lightboxOpen || lightboxExiting)) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [lightboxOpen, lightboxExiting]);

  useEffect(() => {
    let cancelled = false;

    const loadTimelineData = async () => {
      try {
        // 统一读取同一个JSON，通过 *_zh 字段切换中英文
        const path = "/data/timeline.json";
        const res = await fetch(path);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        console.log(`✅ Timeline loaded from ${path} (${data.length} items)`);
        if (cancelled) return;
        setTimelineData(Array.isArray(data) ? data : []);

      } catch (error) {
        console.error("Error loading static timeline JSON:", error);
        if (cancelled) return;
        setTimelineData([]);
      }
    };

    loadTimelineData();
    return () => {
      cancelled = true;
    };
  }, []);

  // 手机端：到达第一个事件节点时显示，回到该节点上方时隐藏
  useEffect(() => {
    const isMobile = window.innerWidth <= MOBILE_DECADE_BREAKPOINT;
    if (!isMobile) {
      // 桌面端始终显示
      setShowDecadeNav(true);
      return;
    }

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // 使用页面绝对滚动节点，而不是视口相对位置，避免移动端浏览器 UI
          // 伸缩时在中途重复穿越阈值，导致导航乱跳。
          const markerEl = firstEventBlockRef.current || firstEventImageRef.current;
          if (!markerEl) {
            // 数据未渲染完成前保持隐藏，避免首屏闪现
            setShowDecadeNav(false);
            ticking = false;
            return;
          }

          const navbarEl = document.querySelector(".navbar");
          const navHeight = navbarEl
            ? navbarEl.getBoundingClientRect().height
            : window.innerWidth <= 600
            ? 64
            : 70;
          const currentScrollY =
            window.pageYOffset || document.documentElement.scrollTop || 0;
          const markerAbsoluteTop =
            markerEl.getBoundingClientRect().top + currentScrollY;
          const triggerScrollY = Math.max(0, markerAbsoluteTop - navHeight - 20);
          const hideScrollY = Math.max(0, triggerScrollY - 48);

          setShowDecadeNav((prev) => {
            const next = prev
              ? currentScrollY >= hideScrollY
              : currentScrollY >= triggerScrollY;
            return next === prev ? prev : next;
          });

          ticking = false;
        });
        ticking = true;
      }
    };
    // 初始执行一次，避免页面初始时先闪现年代条
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [timelineData, MOBILE_DECADE_BREAKPOINT]);

  // 新增：监听滚动，切换DecadeNavBar模式（仅手机端）
  useEffect(() => {
    const isMobile = window.innerWidth <= MOBILE_DECADE_BREAKPOINT;
    if (!isMobile) {
      setShowCompactDecadeBar(false);
      return;
    }

    const handleScroll = () => {
      const markerEl = firstEventBlockRef.current || firstEventImageRef.current;
      if (!markerEl) return;

      const navbarEl = document.querySelector(".navbar");
      const navHeight = navbarEl
        ? navbarEl.getBoundingClientRect().height
        : window.innerWidth <= 600
        ? 64
        : 70;
      const currentScrollY =
        window.pageYOffset || document.documentElement.scrollTop || 0;
      const markerAbsoluteTop =
        markerEl.getBoundingClientRect().top + currentScrollY;
      const compactTriggerScrollY = Math.max(0, markerAbsoluteTop - navHeight - 20);
      setShowCompactDecadeBar(currentScrollY >= compactTriggerScrollY);
    };
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [timelineData, MOBILE_DECADE_BREAKPOINT]);

  const getDecade = (year) => {
    if (!year) return "Unknown";
    const decade = Math.floor(year / 10) * 10;
    return `${decade}s`;
  };

  const localizedTimelineData = useMemo(
    () =>
      Array.isArray(timelineData)
        ? timelineData.map((item) => ({
            ...item,
            eventname:
              language === "CN"
                ? item.eventname_zh || item.eventname
                : item.eventname,
            eventdescription:
              language === "CN"
                ? item.eventdescription_zh || item.eventdescription
                : item.eventdescription,
            meaning:
              language === "CN"
                ? item.meaning_zh || item.meaning
                : item.meaning,
            "Image Copyright": item["Image Copyright"],
          }))
        : [],
    [language, timelineData]
  );

  // 对数据进行排序，按年份升序
  const sortedTimelineData = [...localizedTimelineData].sort(
    (a, b) => a.year - b.year
  );
  // 分组，按照 decade 分组
  const groupedData = sortedTimelineData.reduce((acc, item) => {
    const decade = getDecade(item.year);
    if (!acc[decade]) acc[decade] = [];
    acc[decade].push(item);
    return acc;
  }, {});
  const decades = propDecades || Object.keys(groupedData);
  const priorityImageUrls = new Set(
    sortedTimelineData
      .filter((item) => item.image_url)
      .slice(0, 2)
      .map((item) => item.image_url)
  );
  const lightboxImageMeta = lightboxSrc
    ? timelineImageManifest[lightboxSrc]
    : null;
  const normalizedTargetEventId =
    targetEventId === null || targetEventId === undefined
      ? ""
      : String(targetEventId);

  // Hero event links land here after the async timeline data has rendered.
  // scrollIntoView respects the responsive scroll-margin set on each row.
  useEffect(() => {
    if (
      !normalizedTargetEventId ||
      timelineData === null ||
      lastLocatedEventRef.current === normalizedTargetEventId
    ) {
      return undefined;
    }

    const targetElement = eventRefs.current[normalizedTargetEventId];
    const targetItem = timelineData.find(
      (item) => String(item.eventid) === normalizedTargetEventId
    );
    if (!targetElement || !targetItem) return undefined;

    lastLocatedEventRef.current = normalizedTargetEventId;
    setActiveDecade(getDecade(targetItem.year));

    let secondFrame = null;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        const reduceMotion =
          window.matchMedia &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        targetElement.scrollIntoView({
          behavior: reduceMotion ? "auto" : "smooth",
          block: "start",
        });
        targetElement.focus({ preventScroll: true });
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame !== null) window.cancelAnimationFrame(secondFrame);
    };
  }, [normalizedTargetEventId, timelineData]);

  // decade 区块 refs
  // useEffect(() => {
  //   if (propDecadeRefs) return;
  //   decadeRefs.current = {};
  //   decades.forEach((decade) => {
  //     decadeRefs.current[decade] = React.createRef();
  //   });
  // }, [decades.length, propDecadeRefs]);

  // 监听滚动，自动高亮当前 decade（仅在页面滚动停止后判断）
  useEffect(() => {
    if (decades.length === 0 || propSetActiveDecade) return;
    let scrollTimeout = null;
    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        let found = decades[0];
        const topValue = isMenuHidden ? 80 : 120;
        const compensation = 400;
        for (let i = 0; i < decades.length; i++) {
          const ref = decadeRefs.current[decades[i]];
          const nextRef = decadeRefs.current[decades[i + 1]];
          if (ref) {
            const rect = ref.getBoundingClientRect();
            const nextRect = nextRef ? nextRef.getBoundingClientRect() : null;
            if (
              rect.top <= topValue + compensation &&
              (!nextRect || nextRect.top > topValue + compensation)
            ) {
              found = decades[i];
            }
          }
        }
        setActiveDecade(found);
      }, 120); // 120ms后判定为“滚动停止”
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [decades, propSetActiveDecade, isMenuHidden]);

  // 点击 decade 按钮跳转（恢复为立即高亮目标decade）
  const handleDecadeClick = (decade) => {
    setActiveDecade(decade);
    const ref = decadeRefs.current[decade];
    if (ref) {
      const rect = ref.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      // 计算动态顶部占位：优先使用粘性年代导航的top值，否则退回到原来的阈值
      const decadeBarEl = document.querySelector(".decade-navbar");
      const computedTop = decadeBarEl
        ? parseInt(window.getComputedStyle(decadeBarEl).top || "0", 10)
        : 0;
      const fallbackTop = isMenuHidden ? 80 : 120;
      const topValue = computedTop > 0 ? computedTop : fallbackTop;
      const isMobileView = window.innerWidth <= MOBILE_DECADE_BREAKPOINT;
      // 手机端：在原有基础上再向下滑动一些；
      // 电脑端：保持原始逻辑不变
      let offset;
      if (isMobileView) {
        const extraOffset = -110; // 额外下移像素（可按需微调）
        offset = rect.top + scrollTop - topValue + extraOffset;
      } else {
        // 原始桌面端定位逻辑
        offset = rect.top + scrollTop - topValue - 200;
      }
      window.scrollTo({ top: offset, behavior: "smooth" });
    }
  };

  // 统一桌面端和移动端渲染结构
  return (
    <div className={`timeline-container ${language === "CN" ? "timeline-cn" : ""}`}>
      <div className="timeline-header">
        <h1>{language === "CN" ? "摄影时间线" : "TIMELINE"}</h1>
        <p className="intro-text">
          {language === "CN"
            ? "摄影史建立在两项关键发现之上：其一是暗箱成像原理；其二是某些物质在受光后会发生可见变化。"
            : "The history of photography began with the discovery of two critical principles: The first is camera obscura image projection; the second is the discovery that some substances are visibly altered by exposure to light."}
        </p>
      </div>
      <DecadeNavBar
        decades={decades}
        activeDecade={activeDecade}
        onDecadeClick={handleDecadeClick}
        isMenuHidden={isMenuHidden}
        showDecadeNav={showDecadeNav}
        compact={showCompactDecadeBar}
      />
      {timelineData === null ? null : Object.entries(groupedData).length === 0 ? (
        <div className="no-data-message">
          {language === "CN" ? "暂无时间线数据。" : "No timeline data available."}
        </div>
      ) : (
        <>
          {Object.entries(groupedData).map(([decade, events], index) => {
            return (
            <div
              className="decade-block"
              key={decade}
              ref={(el) => {
                decadeRefs.current[decade] = el;
                if (index === 0) firstEventBlockRef.current = el;
              }}
              id={`decade-${decade}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="decade-left">
                <span className="decade-label">{decade}</span>
              </div>
              <div className="decade-events">
                {events.map((item, eventIndex) => {
                  const eventId = String(item.eventid ?? eventIndex);
                  const isLinkedEvent =
                    normalizedTargetEventId === eventId;

                  return (
                  <div
                    className={`event-row${
                      isLinkedEvent ? " timeline-link-target" : ""
                    }`}
                    id={`event-${eventId}`}
                    key={item.eventid || eventIndex}
                    ref={(element) => {
                      eventRefs.current[eventId] = element;
                    }}
                    tabIndex={isLinkedEvent ? -1 : undefined}
                  >
                    <div className="timeline-center">
                      <h2 className="event-title">{item.eventname}</h2>
                      <p className="event-time">
                        {language === "CN" ? "年份：" : "Year: "} {item.year}
                      </p>
                      <p className="event-description">
                        {item.eventdescription}
                        {item.meaning ? ` ${item.meaning}` : ""}
                      </p>
                      {item["Image Copyright"] && (
                        <p className="event-copyright">
                          {item["Image Copyright"]}
                        </p>
                      )}
                    </div>
                    <div className="timeline-right">
                      {item.image_url && (
                        <TimelineImage
                          ref={(el) => {
                            if (index === 0 && eventIndex === 0) {
                              firstEventImageRef.current = el;
                            }
                          }}
                          src={item.image_url}
                          alt={item.eventname}
                          imageMeta={timelineImageManifest[item.image_url]}
                          language={language}
                          priority={priorityImageUrls.has(item.image_url)}
                          onClick={() =>
                            openLightbox(
                              item.image_url,
                              item.eventname,
                              item["Image Copyright"]
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter")
                              openLightbox(
                                item.image_url,
                                item.eventname,
                                item["Image Copyright"]
                              );
                          }}
                        />
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
            );
          })}
          <div
            className="timeline-continuation"
            aria-label={
              language === "CN"
                ? "时间线未完待续，更多内容正在整理中"
                : "Timeline to be continued, more entries are in progress"
            }
          >
            <div className="continuation-year" aria-hidden="true"></div>
            <div className="continuation-event" aria-hidden="true">
              <div className="continuation-copy">
                <span className="skeleton-block skeleton-title-line"></span>
                <span className="skeleton-block skeleton-title-line secondary"></span>
                <span className="skeleton-block skeleton-copy-line short"></span>
                <span className="skeleton-block skeleton-copy-line"></span>
                <span className="skeleton-block skeleton-copy-line medium"></span>
                <span className="skeleton-block skeleton-copy-line faint"></span>
              </div>
              <div className="continuation-image">
                <span className="skeleton-block skeleton-image-block"></span>
              </div>
            </div>
            <span className="continuation-note" aria-hidden="true">
              {language === "CN"
                ? "光影档案，未完待续……"
                : "THE ARCHIVE CONTINUES..."}
            </span>
          </div>
        </>
      )}
      {(lightboxOpen || lightboxExiting) && (
        <div
          className={`lightbox-overlay ${
            lightboxExiting ? "lightbox-overlay-exit" : "lightbox-overlay-enter"
          }`}
          role="dialog"
          aria-modal="true"
          aria-label={lightboxAlt || "Image preview"}
          onClick={closeLightbox}
        >
          <div
            className={`lightbox-content ${
              lightboxExiting ? "lightbox-exit" : "lightbox-enter"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {lightboxSrc && (
              <TimelineImage
                src={lightboxSrc}
                alt={lightboxAlt || "Timeline image"}
                imageMeta={lightboxImageMeta}
                language={language}
                lightbox
              />
            )}
            {lightboxCopyright && (
              <p className="lightbox-copyright">{lightboxCopyright}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TimelinePage;
